import { Copy, ShieldCheck, GitBranch, Activity, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { shorten } from "../../lib/trace";

export const Panel = ({ title, eyebrow, icon: Icon, right, children, className = "", testId }) => (
  <section data-testid={testId} className={`min-w-0 rounded-xl border border-trace-line bg-trace-card/70 p-5 sm:p-6 ${className}`}>
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 break-words">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2 className="mt-1 flex items-center gap-2 text-base font-medium text-white md:text-lg">
          {Icon && <Icon size={16} className="text-trace-green" aria-hidden="true" />}
          {title}
        </h2>
      </div>
      {right}
    </header>
    {children}
  </section>
);

export const AddressChip = ({ address, testId, link = true, kind = "account", chars = 4 }) => {
  if (!address) return <span className="font-mono text-xs text-trace-dim">—</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  const label = shorten(address, chars);
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-slate-200">
      {link ? (
        <a href={`https://solscan.io/${kind}/${address}`} target="_blank" rel="noopener noreferrer nofollow"
          title={address} data-testid={testId} className="inline-flex items-center gap-1 transition-colors hover:text-trace-green">
          {label} <ExternalLink size={10} className="opacity-50" />
        </a>
      ) : (
        <span title={address} data-testid={testId}>{label}</span>
      )}
      <button onClick={copy} aria-label="Copy address" data-testid={testId ? `${testId}-copy` : undefined}
        className="text-trace-dim transition-colors hover:text-trace-green">
        <Copy size={12} />
      </button>
    </span>
  );
};

const BASIS = {
  confirmed: ["Confirmed on-chain", "border-trace-green/30 bg-trace-green/5 text-trace-green", ShieldCheck],
  inferred: ["Inferred", "border-amber-500/40 bg-amber-500/5 text-amber-300", GitBranch],
  market: ["Market data", "border-sky-500/30 bg-sky-500/5 text-sky-300", Activity],
};

export const BasisBadge = ({ basis }) => {
  const [label, cls, Icon] = BASIS[basis] || BASIS.market;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cls}`}>
      <Icon size={10} aria-hidden="true" /> {label}
    </span>
  );
};

const LEVEL = {
  high: "border-red-500/40 bg-red-500/10 text-red-400",
  medium: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  low: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  pass: "border-trace-line bg-trace-panel text-trace-dim",
};

export const LevelBadge = ({ level, testId }) => (
  <span data-testid={testId} className={`inline-flex w-[68px] justify-center rounded-md border py-0.5 font-mono text-[10px] uppercase tracking-wider ${LEVEL[level]}`}>
    {level === "pass" ? "Clear" : level}
  </span>
);

export const Stat = ({ label, value, sub, testId, tone }) => (
  <div className="rounded-lg border border-trace-line bg-trace-panel p-4" data-testid={testId}>
    <p className="eyebrow">{label}</p>
    <p className={`mt-2 font-heading text-xl font-medium tracking-tight ${tone || "text-white"}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-trace-dim">{sub}</p>}
  </div>
);
